import React from "react";
import { reduxForm, Field, FieldArray } from "redux-form";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import i18next from "i18next";

import { SelectInput } from "../SelectInput";
import { TextInput } from "../TextInput";
import { CheckboxInput } from "../CheckboxInput";
import { TextInputPhone } from "../TextInputPhone";
import { PageLevelAccess } from "../PageLevelAccess";
import { FormatHelper, ValidationHelper } from "../../helpers";
import { LocalesService, UsersService } from "../../services";

import config from "config";

const TEMPERATURE_DISPLAY_UNITS = [
  { value: "C", label: "Celsius" },
  { value: "F", label: "Fahrenheit" }
];

const validate = (values) => {
  let errors = {};

  if (!values.firstName) {
    errors.firstName = i18next.t("messages.please-enter-first-name");
  }

  if (!values.lastName) {
    errors.lastName = i18next.t("messages.please-enter-last-name");
  }

  if (values.password && values.password.length < 8) {
    errors.password = i18next.t("messages.password-eight-char-minimum");
  }
  if (values.password && !values.repeat_password) {
    errors.repeat_password = i18next.t("messages.please-enter-repeat-password");
  }
  if (!(values.repeat_password === values.password)) {
    errors.repeat_password = i18next.t("messages.passwords-do-not-match");
  }

  if (!values.username) {
    errors.username = i18next.t("messages.please-enter-user-name");
  } else if (!ValidationHelper.validateUsername(values.username)) {
    errors.username = i18next.t("messages.username-invalid");
  }

  if (values.roles) {
    errors.roles = {};

    if (values.roles.access_role && values.roles.access_role.length === 0) {
      errors.roles.access_role = i18next.t("messages.please-select-role");
    }

    if (values.roles.action_role && values.roles.action_role.length === 0) {
      errors.roles.action_role = i18next.t("messages.please-select-role");
    }
  }

  if (!values.locale) {
    errors.locale = i18next.t("messages.please-select-language");
  }

  return errors;
};

@connect(({form}) => { ({ form: form }) })
class UserEditForm extends React.Component {

  static propTypes = {
    user: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      password: PropTypes.string,
      temperatureDisplayUnit: PropTypes.string.isRequired,
      enterprise: PropTypes.Object,
      notificationPreferences: PropTypes.array,
    }),
    availableRoles: PropTypes.array.isRequired,
    roleTypes: PropTypes.array.isRequired,
  };

  constructor(props) {
    super(props);

    const { user, roleTypes, emailPreferences, smsPreferences, voicePreferences } = props;

    const voiceNotificationPrefixPref = user.enterprise.enterprisePreferences.find((preference) => preference.token === "VOICE_NOTIFICATION_PREFIX");

    let roleOptionsByType = {};
    roleTypes.forEach((roleType) => {
      roleOptionsByType[roleType.token] = user.roles.filter((role) => role.roleType.token === roleType.token).map((role) => ({ value: role.id, label: role.name, role: role }));
    });

    const userNotificationPreferences = user.notificationPreferences == null ? [] : user.notificationPreferences;

    this.state = {
        showPassword: user.password != null,
        localNotificationPreferences: userNotificationPreferences,
        notificationPreferences: userNotificationPreferences,
        rolesByType: roleTypes.map((roleType) => ({ type: roleType, options: this.filterAvailableRoles(roleType) })),
        showDeleteModal: false,
        showTestNotificationModal: false,
        usernameReadOnly: user.username ? true : false,
        voicePrefixEnabled: voiceNotificationPrefixPref?.stringValue != null
    };

    let initData = {
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      locale: user.locale.languageTag ? { value: user.locale.languageTag, label: user.locale.description } : null,
      password: user.password,
      repeat_password: user.password,
      enterprise: user.enterprise,
      temperatureDisplayUnit: TEMPERATURE_DISPLAY_UNITS.find((unit) => unit.value == user.temperatureDisplayUnit),
      roles: roleOptionsByType,
      emailPreferences: emailPreferences,
      smsPreferences: smsPreferences,
      voicePreferences: voicePreferences,
      notificationPreferences: userNotificationPreferences.map((pref) => ({ value: pref.id, label: pref.email || pref.sms || pref.voice })),
    };

    this.props.initialize(initData);
  }

  componentDidMount() {
    LocalesService.getAll()
      .then((response) => {
        this.setState({ locales: response.locales.map((locale) => ({ value: locale.languageTag, label: locale.description })) });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  filterAvailableRoles(roleType) {
    return this.props.availableRoles
      .filter((role) => role.roleType.token === roleType.token)
      .map((role) => ({ value: role.id, label: role.name, role: role }));
  }

  updateAvailableRoles(roleType) {
    let updatedRoleTypes = this.state.rolesByType.map((rt) => {
      if ( (roleType.token === "access_role" || roleType.token === "action_role") && (rt.type.token === "access_role" || rt.type.token === "action_role") ) {
        rt.options = this.filterAvailableRoles(rt.type);
      }
      return rt;
    });

    this.setState({ rolesByType: updatedRoleTypes });
  }

  toggleDeleteModal() {
    this.setState({ showDeleteModal: !this.state.showDeleteModal });
  }

  toggleTestNotificationModal() {
    this.setState({
      showTestNotificationModal: !this.state.showTestNotificationModal,
      testNotificationMessage: "",
    });
  }

  prepareTestNotificationModal(userData) {
    const emailPrefs = userData.emailPreferences.filter((pref) => pref.email && pref.email.length);
    const smsPrefs = userData.smsPreferences.filter((pref) => pref.sms && pref.sms !== "+1");
    const voicePrefs = userData.voicePreferences.filter((pref) => pref.voice?.number && pref.voice.number !== "+1");

    if (emailPrefs.length === 0 && smsPrefs.length === 0 && voicePrefs.length === 0) {
      alert(i18next.t("messages.no-notification-preferences-set"));
      return;
    }

    this.setState({
      emailPreferences: emailPrefs,
      smsPreferences: smsPrefs,
      voicePreferences: voicePrefs,
    });

    this.toggleTestNotificationModal();
  }

  handleTestNotificationPreferences({ emailPreferences, smsPreferences, voicePreferences }) {
    this.setState({ localErrorMessage: undefined });

    const activeEmailPrefs = emailPreferences.filter((pref) => pref.email && pref.email.length && pref.testEnabled);
    const activeSmsPrefs = smsPreferences.filter((pref) => pref.sms && pref.sms !== "+1" && pref.sms !== "" && pref.testEnabled);
    const activeVoicePrefs = voicePreferences.filter((pref) => pref.voice && pref.voice !== "+1" && pref.voice !== "" && pref.testEnabled);

    let notificationPreferences = FormatHelper.formatPhoneNumbers(activeSmsPrefs)
      .concat(FormatHelper.formatPhoneNumbers(activeVoicePrefs, "voice"))
      .concat(activeEmailPrefs);

    if (notificationPreferences.length > 0) {
      UsersService.testNotificationPreferences(notificationPreferences)
          .then((_) => {
            const emailSent = activeEmailPrefs.length > 0 ? <p>{ i18next.t("messages.email-sent-to", { recipients: activeEmailPrefs.map((pref) => pref.email).join(",") })} </p> : "";
            const smsSent = activeSmsPrefs.length > 0 ? <p>{ i18next.t("messages.sms-sent-to", { recipients: activeSmsPrefs.map((pref) => pref.sms).join(",") })}</p> : "";
            const voiceSent = activeVoicePrefs.length > 0 ? <p>{ i18next.t("messages.voice-call-to", { recipients: activeVoicePrefs.map((pref) => pref.voice?.number).join(",") })}</p> : "";

            this.setState({ testNotificationMessage: (<div>{emailSent} {smsSent} {voiceSent}</div>) });
          })
          .catch((error) => {
            console.error(error);
            this.setState({ localErrorMessage: i18next.t("messages.error-testing-notification-preferences") });
          });
    } else {
      this.setState({ testNotificationMessage: i18next.t("messages.no-notification-preferences-selected") });
    }
  }

  render() {
    const { handleSubmit, onArchive, form, user } = this.props;

    const userFirstLast = form.UserEditForm?.values ? `${form.UserEditForm.values.firstName} ${form.UserEditForm.values.lastName}` : `${user.firstName} ${user.lastName}`;

    const deleteButton = onArchive !== undefined ? (
        <li className="pull-right">
          <a className="btn btn-danger" onClick={() => this.toggleDeleteModal()}>{i18next.t("delete")}</a>
        </li>
      ) : ("");

    return (
      <form className="user-form" onSubmit={handleSubmit}>
        
      <div className="row">
          <div className="col-sm-12 col-md-6 col-lg-6">
            <Field
              name="firstName"
              type="text"
              component={TextInput}
              label={i18next.t("form-labels.first-name")}
            />
          </div>
          <div className="col-sm-12 col-md-6 col-lg-6">
            <Field
              name="lastName"
              type="text"
              component={TextInput}
              label={i18next.t("form-labels.last-name")}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12 col-md-6 col-lg-6">
            <Field
              name="username"
              type="text"
              component={TextInput}
              label={i18next.t("form-labels.username")}
              readOnly={this.state.usernameReadOnly}
            />
          </div>
          <div className="col-sm-12 col-md-6 col-lg-6">
            <label>{i18next.t("pages.users.language")}</label>
            <Field
              name="locale"
              options={this.state.locales}
              isSearchable={true}
              clearable={false}
              component={SelectInput}
            />
          </div>
        </div>

        <div className="row" style={{paddingBottom: 20}}>
          <div className="col-sm-12 col-md-6 col-lg-6">
            <label>Temperature Display Unit</label>
            <Field
                name="temperatureDisplayUnit"
                options={TEMPERATURE_DISPLAY_UNITS}
                isSearchable={false}
                clearable={false}
                component={SelectInput}
              />
          </div>
          <div className="col-sm-12 col-md-6 col-lg-6">
            &nbsp;
          </div>
        </div>

        <PageLevelAccess to="/admin/users#password">
          <div className="op-content-box-level-2 action-wrap">
            <div className="row">
              <div className="col-sm-12">
                <ul className="list-inline clearfix remove-margin-bottom actions-header">
                  <li className="pull-left">
                    <h4 className="remove-margin-bottom">
                      {i18next.t("form-labels.password")}
                    </h4>
                  </li>
                </ul>
              </div>
              <div className="col-sm-12 oc-mini-card">
                <div className="inner">
                  <div className="row">
                    <div className="form-group col-sm-12 col-lg-6">
                      <Field
                        name="password"
                        type="text"
                        component={TextInput}
                        label={i18next.t("form-labels.new-password")}
                      />
                      <Field
                        name="repeat_password"
                        type="text"
                        component={TextInput}
                        label={i18next.t("form-labels.repeat-new-password")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <br />
        </PageLevelAccess>
        
        <div className="op-content-box-level-2 action-wrap">
          <div className="row">
            <div className="col-sm-12">
              <ul className="list-inline clearfix remove-margin-bottom actions-header">
                <li className="pull-left">
                  <h4 className="remove-margin-bottom">
                    {i18next.t("form-labels.roles")}
                  </h4>
                </li>
              </ul>
            </div>
            <div className="col-sm-12 oc-mini-card">
              <div className="inner">
                {this.state.rolesByType.map((element, index) => (
                  <div className="row" key={index}>
                    <div className="form-group col-sm-12 col-lg-6">
                      <label>
                        {i18next.t(`role-types.${element.type.token}`)}
                      </label>
                      <Field
                        name={`roles[${element.type.token}]`}
                        options={element.options}
                        component={SelectInput}
                        isMulti
                        isSearchable={true}
                        onChange={(event, newValue) => this.updateAvailableRoles(element.type)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <br />
        
        <div className="op-content-box-level-2 action-wrap">
          <div className="row">
            <div className="col-sm-12">
              <ul className="list-inline clearfix remove-margin-bottom actions-header">
                <li className="pull-left">
                  <h4 className="remove-margin-bottom">
                    {i18next.t("pages.users.notifications-and-report-info")}
                  </h4>
                </li>
                <li className="pull-right">
                  <button
                    className="btn btn-default btn-sm"
                    type="button"
                    onClick={handleSubmit((values) => this.prepareTestNotificationModal(values))}
                  >
                    {i18next.t("pages.users.send-test-message")}
                  </button>
                </li>
              </ul>
            </div>
            <div className="col-sm-12 oc-mini-card">
              <div className="inner">
                <div className="row">
                  <div className="form-group col-sm-12 col-md-6 col-lg-6">
                    <label>{i18next.t("form-labels.email")}</label>
                    <FieldArray name="emailPreferences" type="email" component={NotificationEntry} />
                  </div>
                  <div className="col-sm-12 col-md-6 col-lg-6">
                    <div className="form-group">
                      <label>{i18next.t("form-labels.sms-number")}</label>
                      <FieldArray name="smsPreferences" type="sms" component={NotificationEntry} />
                    </div>
                    <div className="form-group">
                      <label>{i18next.t("form-labels.voice-number")}</label>
                      <FieldArray name="voicePreferences" type="voice" voicePrefixEnabled={this.state.voicePrefixEnabled} component={NotificationEntry} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12">
            <hr />
            <ul className="list-inline">
              <li>
                <button type="submit" className="btn btn-primary">
                  {i18next.t("form-labels.save")}
                </button>
              </li>
              <li>
                <Link to={ this.props.backLinkWithParams ? this.props.backLinkWithParams.link : `/admin/users` }>
                  <button className="btn btn-default">{i18next.t("form-labels.cancel")}</button>
                </Link>
              </li>
              <PageLevelAccess to="/admin/users/edit/:userId#delete">
                {deleteButton}
              </PageLevelAccess>
            </ul>
          </div>
        </div>

        <Modal show={this.state.showDeleteModal} onHide={this.toggleDeleteModal.bind(this)}>
          <Modal.Header closeButton>
            <Modal.Title>
              {i18next.t("pages.users.delete-user", { name: userFirstLast })}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{i18next.t("messages.are-you-sure-delete")}</p> <br />
          </Modal.Body>
          <Modal.Footer>
            <a className="btn btn-danger" onClick={this.props.onArchive}>
              {i18next.t("delete")}
            </a>
            <Button onClick={this.toggleDeleteModal.bind(this)}>
              {i18next.t("form-labels.cancel")}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={this.state.showTestNotificationModal} onHide={this.toggleTestNotificationModal.bind(this)}>
          <Modal.Header closeButton>
            <Modal.Title>
              {i18next.t("pages.users.notification-test")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group col-sm-12 col-md-6 col-lg-6">
              <label>{i18next.t("form-labels.email")}</label>
              <FieldArray name="emailPreferences" type="email" component={NotificationTestSelection} />
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6">
              <div className="form-group">
                <label>{i18next.t("form-labels.sms-number")}</label>
                <FieldArray name="smsPreferences" type="sms" component={NotificationTestSelection} />
              </div>
              <div className="form-group">
                <label>{i18next.t("form-labels.voice-number")}</label>
                <FieldArray name="voicePreferences" type="voice" component={NotificationTestSelection} />
              </div>
            </div>
            {this.state.testNotificationMessage}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleSubmit((values) => this.handleTestNotificationPreferences(values) )}>
              {i18next.t("pages.users.send-test-message")}
            </Button>
            <Button onClick={this.toggleTestNotificationModal.bind(this)}>
              {i18next.t("form-labels.cancel")}
            </Button>
          </Modal.Footer>
        </Modal>
      </form>
    );
  }
}

class NotificationTestSelection extends React.Component {
  shouldComponentUpdate(nextProps, nextState) {
    return (
      JSON.stringify(this.props) !== JSON.stringify(nextProps) ||
      JSON.stringify(this.state) !== JSON.stringify(nextState)
    );
  }

  render() {
    let { fields } = this.props;
    const fieldName = this.props.type;
    const filteredFields = fields
        .getAll()
        .filter(
            (notification) =>
                (fieldName === "voice" && notification[fieldName].number !== "+1" && notification[fieldName].number !== "") ||
                (fieldName !== "voice" && notification[fieldName] !== "+1" && notification[fieldName] !== "")
        );

    return (
      <div className="margin-bottom-sm">
        {filteredFields.length > 0 ? (
          fields.map((notification, index, notificationValue) =>
              notificationValue.get(index)[fieldName] &&
              notificationValue.get(index)[fieldName] !== "" &&
              (
                (fieldName === 'voice' && notificationValue.get(index)[fieldName].number !== "+1" && notificationValue.get(index)[fieldName].number !== "") || 
                (fieldName !== 'voice' && notificationValue.get(index)[fieldName] !== "+1" && notificationValue.get(index)[fieldName] !== "")
              ) && (
                <div key={index} className="margin-bottom-sm">
                  <div className="input-group">
                    <span className="input-group-addon group-addon-checkbox">
                      <Field
                        name={`${notification}.testEnabled`}
                        type="checkbox"
                        className="notification-input"
                        component={CheckboxInput}
                        defaultChecked={true}
                      />
                      {fieldName === 'voice' && `${notificationValue.get(index)[fieldName]?.number}${notificationValue.get(index)[fieldName]?.sendVoicePrefix ? ' (IVR Prefix Enabled)' : ''}` }
                      {fieldName !== 'voice' && notificationValue.get(index)[fieldName]}
                    </span>
                  </div>
                </div>
              )
          )
        ) : (
          <div>
            <em>none</em>
          </div>
        )}
      </div>
    );
  }
}

class NotificationEntry extends React.Component {
  pushEntry(fieldName, fields) {
    let emptyField = { active: true, testEnabled: false };
    emptyField[fieldName] = "";
    fields.push(emptyField);
  }

  removeEntry(index, fields) {
    fields.remove(index);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      JSON.stringify(this.props) !== JSON.stringify(nextProps) ||
      JSON.stringify(this.state) !== JSON.stringify(nextState)
    );
  }

  render() {
    let { fields } = this.props;
    const fieldName = this.props.type;
    if (fields.length === 0) {
      this.pushEntry(fieldName, fields);
    }

    return (
      <div className="margin-bottom-sm">
        {fields.map((notification, index) => (
          <div key={index} className="margin-bottom-sm">
            <div className="input-group">
              <Field
                name={`${notification}.${fieldName}${this.props.type === 'voice' ? '.number' : ''}`}
                type="text"
                className="notification-input"
                component={ this.props.type === "email" ? TextInput : TextInputPhone }
                validate={ this.props.type === "email" ? ValidationHelper.validateEmail : ValidationHelper.validatePhoneNumber }
                autoComplete="off"
              />

              {
                 this.props.type === "voice" && 
                 <Field
                    name={`${notification}.${fieldName}.sendVoicePrefix`}
                    type="checkbox"
                    className="inline"
                    component={CheckboxInput}
                    label={'Enable IVR Prefix'}
                  />
              }

              <span className="input-group-addon">
                {index !== 0 ? (
                  <img
                    src={`${config.WEB_BASE_URL}/images/icon/delete-trash-can.svg`}
                    name="delete"
                    alt={i18next.t("delete")}
                    onClick={() => this.removeEntry(index, fields)}
                  />
                ) : null}
                {index + 1 === fields.length ? (
                  <img
                    src={`${config.WEB_BASE_URL}/images/icon/add-button.svg`}
                    name="add"
                    alt={i18next.t("add")}
                    onClick={() => this.pushEntry(fieldName, fields)}
                  />
                ) : null}
              </span>

            </div>
          </div>
        ))}
      </div>
    );
  }
}

export default reduxForm({ form: "UserEditForm", enableReinitialize: true, keepDirtyOnReinitialize: true, validate })(UserEditForm);
